import { env } from './env';

/**
 * Application configuration
 * This is the centralized, strongly-typed configuration object
 * All application code should import from this file, NOT from env.ts or import.meta.env
 */
export const Config = {
  /**
   * API Configuration
   */
  api: {
    baseUrl: env.VITE_API_BASE_URL,
  },
  
  /**
   * Environment
   */
  environment: env.VITE_ENVIRONMENT,
  
  /**
   * Authentication Configuration
   */
  auth: {
    enabled: !env.VITE_SKIP_AUTH,
    demoMode: env.VITE_DEMO_MODE,
    localDev: env.VITE_LOCAL_DEV,
    azure: {
      clientId: env.VITE_AZURE_CLIENT_ID || '',
      tenantId: env.VITE_AZURE_TENANT_ID || '',
      redirectUri: env.VITE_AZURE_REDIRECT_URI || window.location.origin,
    },
    demoLoginHint: env.VITE_DEMO_LOGIN_HINT,
    demoPasswordHint: env.VITE_DEMO_PASSWORD_HINT,
  },
  
  /**
   * Security Settings
   * Note: Session timeout is hardcoded to 30 minutes (CJIS 5.5.5) in
   * useSessionTimeout.ts and is NOT configurable via environment variables.
   */
  security: {
    enforceHttps: env.VITE_ENFORCE_HTTPS,
  },
  
  /**
   * External Services
   */
  external: {
    contactUrl: env.VITE_CONTACT_URL,
    supportEmail: env.VITE_SUPPORT_EMAIL,
    documentationUrl: env.VITE_DOCUMENTATION_URL,
  },
  
  /**
   * Feature Flags
   */
  features: {
    chatbot: env.VITE_ENABLE_CHATBOT,
    analytics: env.VITE_ENABLE_ANALYTICS,
  },
  
  /**
   * Logging Configuration
   */
  logging: {
    level: env.VITE_LOG_LEVEL,
    enableApiLogging: env.VITE_ENABLE_API_LOGGING,
    enablePerformanceMonitoring: env.VITE_ENABLE_PERFORMANCE_MONITORING,
  },
} as const;

/**
 * Type-safe config access
 */
export type AppConfig = typeof Config;

/**
 * Helper to check if running in development
 */
export const isDevelopment = Config.environment === 'local' || Config.environment === 'development';

/**
 * Helper to check if running in production
 */
export const isProduction = Config.environment === 'production';

/**
 * Helper to check if running in demo mode
 */
export const isDemoMode = Config.auth.demoMode;

/**
 * Helper to check if authentication is enabled
 */
export const isAuthEnabled = Config.auth.enabled;

/**
 * Helper to check if running in Vite dev mode (HMR enabled)
 */
export const isViteDev = import.meta.env.DEV;

/**
 * Helper to check if running in Vite production build
 */
export const isViteProd = import.meta.env.PROD;
